// src/components/RPA/buttons/WorkflowAssignmentButton.js
import React, { useState } from 'react';
import axios from 'axios';

const api = process.env.REACT_APP_API;

const WorkflowAssignmentButton = ({ docId, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState(null);
  const [err, setErr] = useState('');

  const run = async () => {
    if (!docId) return;
    setBusy(true);
    setErr('');
    setPayload(null);

    try {
      const res = await axios.post(
        `${api}/admin/rpa/workflow/assign`,
        { docId },
        { withCredentials: true, validateStatus: () => true }
      );

      if (res.status === 200) {
        setPayload(res.data || {});
      } else {
        setErr(res.data?.message || `Unexpected status ${res.status}`);
      }

      setOpen(true);
      if (typeof onDone === 'function') onDone(); // refresh doc list
    } catch (e) {
      console.error('[RPA] workflow assignment failed:', e);
      setErr('Workflow assignment failed. Check backend logs.');
      setOpen(true);
    } finally {
      setBusy(false);
    }
  };

  const renderWorkflowInfo = () => {
    if (!payload?.workflow) return null;

    const workflow = payload.workflow;
    
    return (
      <div style={workflowBox}>
        <div style={{ marginBottom: 8 }}>
          <strong>{workflow.templateName}</strong>
        </div>
        <div style={{ fontSize: 13, color: '#555', marginBottom: 8 }}>
          {workflow.description}
        </div>
        
        <div style={{ display: 'grid', gap: 4, fontSize: 12 }}>
          <div><strong>Template:</strong> {workflow.templateId}</div>
          <div><strong>Sampling Required:</strong> {workflow.samplingRequired ? 'Yes (Statistical)' : 'No (Full Population)'}</div>
          <div><strong>Assigned:</strong> {new Date(workflow.assignedAt).toLocaleString()}</div>
        </div>

        {workflow.steps && workflow.steps.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <strong style={{ fontSize: 12 }}>Workflow Steps:</strong>
            <div style={{ marginTop: 4 }}>
              {workflow.steps.map((step, idx) => (
                <div key={step.id} style={stepStyle}>
                  <span style={{ 
                    width: 16, 
                    height: 16, 
                    borderRadius: '50%', 
                    background: step.status === 'completed' ? '#10b981' : '#6b7280',
                    color: 'white',
                    fontSize: 10,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: 8
                  }}>
                    {idx + 1}
                  </span>
                  <span style={{ fontSize: 12 }}>{step.name}</span>
                  <span style={{ 
                    fontSize: 10, 
                    color: step.status === 'completed' ? '#10b981' : '#6b7280',
                    marginLeft: 'auto',
                    textTransform: 'uppercase'
                  }}>
                    {step.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {payload.nextActions && payload.nextActions.length > 0 && (
          <div style={{ marginTop: 12, padding: 8, background: '#f0f9ff', borderRadius: 6 }}>
            <strong style={{ fontSize: 12, color: '#0369a1' }}>Next Actions:</strong>
            <div style={{ marginTop: 4 }}>
              {payload.nextActions.map((action, idx) => (
                <div key={idx} style={{ fontSize: 12, color: '#0369a1', marginTop: 2 }}>
                  • {action.description}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <span style={{ display: 'inline-block' }}>
      <button onClick={run} disabled={busy}>
        {busy ? 'Assigning…' : 'Assign Workflow'}
      </button>

      {open && (
        <div style={panel}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <strong>Workflow Assignment</strong>
            <button onClick={() => setOpen(false)} style={{ padding: '2px 8px' }}>
              Close
            </button>
          </div>

          {err && <p style={{ color: 'crimson' }}>{err}</p>}

          {!err && payload && (
            <>
              {renderWorkflowInfo()}
              
              {/* Raw JSON for debugging */}
              <details style={{ marginTop: 12 }}>
                <summary style={{ fontSize: 12, color: '#666', cursor: 'pointer' }}>
                  Raw Response (Debug)
                </summary>
                <pre style={{ whiteSpace: 'pre-wrap', margin: '8px 0 0 0', fontSize: 11, background: '#f8f9fa', padding: 8, borderRadius: 4 }}>
                  {JSON.stringify(payload, null, 2)}
                </pre>
              </details>
            </>
          )}
        </div>
      )}
    </span>
  );
};

const panel = {
  marginTop: 8,
  background: '#f8f9fb',
  border: '1px solid #e2e5ea',
  padding: 10,
  borderRadius: 8,
  maxWidth: 640
};

const workflowBox = {
  background: '#fff',
  border: '1px solid #e6e9ef',
  padding: 12,
  borderRadius: 8,
  marginBottom: 10
};

const stepStyle = {
  display: 'flex',
  alignItems: 'center',
  marginTop: 4,
  padding: '4px 0'
};

export default WorkflowAssignmentButton;